import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Square, Video, X } from "lucide-react";
import {
  normalizeCaptureTables,
  tableIsDesired,
  tableIsRecording,
  tableIsStarting,
} from "../../utils/championshipCaptureState";
import { ChampionshipMatchOnTable } from "../../components/championship/ChampionshipTableAssign";
import { rubberMatchupLabel } from "../../utils/championshipTableAssign";

function camRoleLabel(cam, t) {
  if (cam?.camera_role === "score" || cam?.angle_label === "scorecam") {
    return t("cmCamPositionScorecam", "Scorecam");
  }
  if (cam?.angle_label === "referee") {
    return t("cmCamPositionReferee", "Referee");
  }
  return t("cmCamPositionSpectator", "Spectator");
}

function statusLabel(captureState, table, t) {
  if (tableIsRecording(captureState, table)) {
    return t("cmRecordingTable", "Recording · Table {{n}}", { n: table });
  }
  if (tableIsStarting(captureState, table)) {
    return t("cmRecordingStarting", "Starting…");
  }
  if (tableIsDesired(captureState, table)) {
    return t("cmRecordingDesired", "Desired");
  }
  return t("cmCameraWarmup", "warmup");
}

/**
 * Staff Control Room: truthful per-table camera + recording status and actions.
 */
export default function ChampionshipControlRoom({
  snapshot,
  captureState,
  scoreWatchByTable,
  canModerate = false,
  canAssign = false,
  assignBusy = false,
  recordBusy = false,
  onStartTable,
  onStopTable,
  onStartAllLive,
  onForceRelease,
  onAssignMatch,
}) {
  const { t } = useTranslation();
  const cameras = snapshot?.cameras || [];
  const rubbers = snapshot?.rubbers || [];

  const tables = useMemo(() => {
    const set = new Set();
    for (const cam of cameras) {
      const n = Number(cam.table_number);
      if (Number.isFinite(n) && n > 0) set.add(n);
    }
    for (const r of rubbers) {
      const n = Number(r.table_number);
      if (Number.isFinite(n) && n > 0) set.add(n);
    }
    for (const row of normalizeCaptureTables(captureState)) {
      set.add(row.table_number);
    }
    if (!set.size) {
      set.add(1);
      set.add(2);
    }
    return [...set].sort((a, b) => a - b);
  }, [cameras, rubbers, captureState]);

  if (!canModerate) return null;

  return (
    <section
      className="cm-control-room"
      aria-label={t("cmControlRoom", "Match control room")}
    >
      <header className="cm-control-room__head">
        <h2 className="cm-control-room__title">
          {t("cmControlRoom", "Match control room")}
        </h2>
        <p className="cm-control-room__hint">
          {t(
            "cmControlRoomHint",
            "Put a match on a table that already has a camera. Recording shows only when a phone has confirmed capture."
          )}
        </p>
        {typeof onStartAllLive === "function" ? (
          <button
            type="button"
            className="cm-btn cm-btn--rec"
            disabled={recordBusy}
            onClick={() => void onStartAllLive()}
          >
            <Video size={14} aria-hidden />
            <span className="cm-btn__label">
              {t("cmStartAllLiveTables", "Start all live tables")}
            </span>
          </button>
        ) : null}
      </header>

      <ul className="cm-control-room__tables">
        {tables.map((table) => {
          const tableCams = cameras.filter(
            (c) => Number(c.table_number) === table
          );
          const rubber =
            rubbers.find(
              (r) => Number(r.table_number) === table && r.state === "live"
            ) ||
            rubbers.find(
              (r) => Number(r.table_number) === table && r.state !== "done"
            );
          const matchup = rubber ? rubberMatchupLabel(rubber, snapshot) : "";
          const watch = scoreWatchByTable?.[table];
          const filming = tableIsRecording(captureState, table);
          const starting = tableIsStarting(captureState, table);
          return (
            <li key={table} className="cm-control-room__row">
              <div className="cm-control-room__row-main">
                <strong>
                  {t("cmTable", "Table")} {table}
                </strong>
                <span
                  className={`cm-control-room__badge${
                    filming
                      ? " is-rec"
                      : starting
                        ? " is-starting"
                        : ""
                  }`}
                >
                  {statusLabel(captureState, table, t)}
                </span>
                {matchup ? (
                  <span className="cm-control-room__matchup">{matchup}</span>
                ) : null}
                {watch?.phase && watch.phase !== "idle" ? (
                  <span className="cm-control-room__scorewatch" role="status">
                    {t("cmScoreWatchRoom", "Scorecam")} · {watch.phase}
                    {watch.score_text ? ` · ${watch.score_text}` : ""}
                    {watch.detail ? ` · ${watch.detail}` : ""}
                  </span>
                ) : null}
              </div>
              <ul className="cm-control-room__cams">
                {tableCams.length === 0 ? (
                  <li className="cm-control-room__cam is-empty">
                    {t("cmNoCameraOnTable", "No camera connected")}
                  </li>
                ) : (
                  tableCams.map((cam) => (
                    <li
                      key={cam.camera_id || `${cam.user_id}-${cam.device_id}`}
                      className={`cm-control-room__cam${
                        cam.online || cam.socket_id ? " is-online" : " is-offline"
                      }`}
                    >
                      <Camera size={12} aria-hidden />
                      <span>
                        {cam.device_label ||
                          camRoleLabel(cam, t)}
                        {" · "}
                        {cam.online || cam.socket_id
                          ? t("cmPhoneConnected", "connected")
                          : t("cmCameraOfflineShort", "offline")}
                      </span>
                      {typeof onForceRelease === "function" ? (
                        <button
                          type="button"
                          className="cm-btn cm-btn--ghost cm-btn--icon"
                          title={t("cmForceReleaseCamera", "Release camera")}
                          aria-label={t(
                            "cmForceReleaseCamera",
                            "Release camera"
                          )}
                          onClick={() =>
                            void onForceRelease({
                              camera_id: cam.camera_id,
                              table_number: table,
                              user_id: cam.user_id,
                              device_id: cam.device_id,
                            })
                          }
                        >
                          <X size={14} aria-hidden />
                        </button>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
              {canAssign && typeof onAssignMatch === "function" ? (
                <div className="cm-control-room__assign">
                  <ChampionshipMatchOnTable
                    tableNumber={table}
                    snapshot={snapshot}
                    busy={assignBusy}
                    onAssign={onAssignMatch}
                  />
                </div>
              ) : null}
              <div className="cm-control-room__actions">
                {filming || starting ? (
                  <button
                    type="button"
                    className="cm-btn cm-btn--danger"
                    disabled={recordBusy}
                    onClick={() => void onStopTable?.(table)}
                  >
                    <Square size={14} aria-hidden />
                    <span className="cm-btn__label">
                      {t("cmEndRecordingTable", "End recording · Table {{n}}", {
                        n: table,
                      })}
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="cm-btn cm-btn--rec"
                    disabled={recordBusy || tableCams.length === 0}
                    onClick={() => void onStartTable?.(table)}
                  >
                    <Video size={14} aria-hidden />
                    <span className="cm-btn__label">
                      {t(
                        "cmStartRecordingTable",
                        "Start recording · Table {{n}}",
                        { n: table }
                      )}
                    </span>
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
